(() => {
  /*
    ============================
    Shared helpers
    ============================
  */
  function requestCloseShopAll() {
    window.dispatchEvent(new CustomEvent("wc:close-shop-all"));
  }

  function getScrollY() {
    return window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  function getDocumentHeight() {
    const body = document.body;
    const doc = document.documentElement;

    return Math.max(
      body ? body.scrollHeight : 0,
      body ? body.offsetHeight : 0,
      doc ? doc.clientHeight : 0,
      doc ? doc.scrollHeight : 0,
      doc ? doc.offsetHeight : 0,
    );
  }

  function setupMobileStickyHeader() {
    const stickyHeader = document.querySelector(
      "[data-wc-mobile-sticky-header]",
    );
    const announcement = document.querySelector(".top-chrome__announcement");
    const triggerTarget = announcement
      ? announcement.querySelector(".message-ticker") || announcement
      : null;
    const mobileStickyMQ = window.matchMedia
      ? window.matchMedia("(max-width: 749px)")
      : null;

    if (!stickyHeader || !triggerTarget) return;

    let ticking = false;
    let hideTimer = null;
    const transitionMs = 240;

    function getTriggerScrollY() {
      const rect = triggerTarget.getBoundingClientRect();
      return Math.max(0, getScrollY() + rect.bottom);
    }

    function setStickyHeaderVisibility(shouldShow) {
      if (shouldShow) {
        if (hideTimer) {
          window.clearTimeout(hideTimer);
          hideTimer = null;
        }

        stickyHeader.hidden = false;
        stickyHeader.setAttribute("aria-hidden", "false");

        window.requestAnimationFrame(() => {
          stickyHeader.classList.add("is-visible");
        });

        return;
      }

      stickyHeader.classList.remove("is-visible");
      stickyHeader.setAttribute("aria-hidden", "true");

      if (stickyHeader.hidden || hideTimer) return;

      hideTimer = window.setTimeout(() => {
        hideTimer = null;

        if (!stickyHeader.classList.contains("is-visible")) {
          stickyHeader.hidden = true;
        }
      }, transitionMs);
    }

    function updateStickyHeader() {
      ticking = false;

      const isMobile = mobileStickyMQ
        ? mobileStickyMQ.matches
        : window.innerWidth <= 749;
      const maxScroll = Math.max(0, getDocumentHeight() - window.innerHeight);
      const scrollY = getScrollY();
      const shouldShow =
        isMobile &&
        maxScroll > 0 &&
        scrollY >= getTriggerScrollY() &&
        scrollY <= maxScroll * 0.5;

      setStickyHeaderVisibility(shouldShow);
    }

    function requestStickyHeaderUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateStickyHeader);
    }

    setStickyHeaderVisibility(false);
    requestStickyHeaderUpdate();

    window.addEventListener("scroll", requestStickyHeaderUpdate, {
      passive: true,
    });
    window.addEventListener("resize", requestStickyHeaderUpdate, {
      passive: true,
    });
    window.addEventListener("load", requestStickyHeaderUpdate);

    if (
      mobileStickyMQ &&
      typeof mobileStickyMQ.addEventListener === "function"
    ) {
      mobileStickyMQ.addEventListener("change", requestStickyHeaderUpdate);
    }
  }

  function setupStickyCartBridge() {
    const stickyCart = document.querySelector("[data-wc-sticky-cart]");
    if (!stickyCart) return;

    function getPrimaryCartCount() {
      const primaryCart = document.getElementById("cart-icon-bubble");
      if (!primaryCart) return null;

      const headerBadge = primaryCart.querySelector(".wc-badge");
      if (headerBadge && headerBadge.textContent.trim()) {
        const count = headerBadge.textContent.trim();
        return {
          count,
          label:
            headerBadge.getAttribute("aria-label") || `${count} items in cart`,
        };
      }

      const countBubble = primaryCart.querySelector(".cart-count-bubble");
      if (!countBubble) return null;

      const visibleCount = countBubble.querySelector('[aria-hidden="true"]');
      const hiddenLabel = countBubble.querySelector(".visually-hidden");
      const label = hiddenLabel ? hiddenLabel.textContent.trim() : "";
      const count =
        (visibleCount && visibleCount.textContent.trim()) ||
        (label.match(/\d+/) || [])[0] ||
        "";

      if (!count) return null;

      return {
        count,
        label: label || `${count} items in cart`,
      };
    }

    function syncStickyCartBadge() {
      const cartCount = getPrimaryCartCount();
      let badge =
        stickyCart.querySelector("[data-wc-sticky-cart-badge]") ||
        stickyCart.querySelector(".wc-badge");
      let status = stickyCart.querySelector("[data-wc-sticky-cart-count]");

      if (!cartCount) {
        if (badge) badge.remove();
        if (status) status.remove();
        return;
      }

      if (!badge) {
        badge = document.createElement("span");
        badge.className = "wc-badge";
        stickyCart.appendChild(badge);
      }

      if (!badge.hasAttribute("data-wc-sticky-cart-badge")) {
        badge.setAttribute("data-wc-sticky-cart-badge", "");
      }

      if (badge.textContent.trim() !== cartCount.count) {
        badge.textContent = cartCount.count;
      }

      if (badge.getAttribute("aria-label") !== cartCount.label) {
        badge.setAttribute("aria-label", cartCount.label);
      }

      if (!status) {
        status = document.createElement("span");
        status.className = "visually-hidden";
        status.setAttribute("data-wc-sticky-cart-count", "");
        stickyCart.appendChild(status);
      }

      if (status.textContent !== cartCount.label) {
        status.textContent = cartCount.label;
      }
    }

    let badgeSyncFrame = null;

    function requestStickyCartBadgeSync() {
      if (badgeSyncFrame) return;

      badgeSyncFrame = window.requestAnimationFrame(() => {
        badgeSyncFrame = null;
        syncStickyCartBadge();
      });
    }

    syncStickyCartBadge();

    if (typeof MutationObserver === "function") {
      const observerRoot =
        document.querySelector(".wc-header") || document.body;
      const badgeObserver = new MutationObserver(requestStickyCartBadgeSync);

      badgeObserver.observe(observerRoot, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    window.addEventListener("pageshow", requestStickyCartBadgeSync);
    window.addEventListener("cart:updated", requestStickyCartBadgeSync);
    document.addEventListener("cart:updated", requestStickyCartBadgeSync);
  }

  setupMobileStickyHeader();
  setupStickyCartBridge();

  /*
    ============================
    Store menu logic
    ============================
  */
  const storeRoots = document.querySelectorAll(".wc-store");
  const classesCtaLinks = document.querySelectorAll("[data-wc-classes-cta]");
  const MOBILE_QUERY = "(max-width: 990px)";
  const mobileMQ = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;

  function openStoreDrawer() {
    if (typeof window.openStoreDetailsDrawer === "function") {
      window.openStoreDetailsDrawer();
      return;
    }

    window.dispatchEvent(new CustomEvent("wc:open-store-drawer"));
  }

  function hasFavorite() {
    return !!localStorage.getItem("storeSelectedId");
  }

  function getStoreClassesHref(store) {
    return store && store.slug
      ? `/pages/store-classes/${store.slug}`
      : "/pages/classes";
  }

  function setClassesCtaHref(href) {
    classesCtaLinks.forEach((link) => {
      link.setAttribute("href", href);
    });
  }

  function renderClassesCta(store) {
    setClassesCtaHref(
      store ? getStoreClassesHref(store) : "/pages/store-locator",
    );
  }

  function computeTodayHours(store) {
    if (!store || !store.hours) return "";

    const today = new Date()
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();

    return store.hours[today] || "";
  }

  function getShortStoreName(storeName = "") {
    const isMobile = mobileMQ ? mobileMQ.matches : window.innerWidth <= 990;
    const cleaned = storeName.replace("Woodcraft of ", "");
    return isMobile ? cleaned : cleaned;
  }
  function firstNonEmpty(values) {
    for (const value of values) {
      if (typeof value === "string" && value.trim() !== "") return value;
    }
    return "";
  }

  function safeJSONParse(value) {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch (_err) {
      return null;
    }
  }

  function getStoredFavoriteStoreSnapshot() {
    const id = localStorage.getItem("storeSelectedId");
    if (!id) return null;

    const storedStore = safeJSONParse(
      localStorage.getItem("storeSelectedData"),
    );
    const fallbackName = firstNonEmpty([localStorage.getItem("storeSelected")]);

    const phone = firstNonEmpty([localStorage.getItem("storeSelectedPhone")]);

    if (storedStore && typeof storedStore === "object") {
      return {
        store: {
          id,
          name: storedStore.name || fallbackName,
          slug: storedStore.slug || "",
          address: storedStore.address || "",
          phone_number: phone || storedStore.phone_number || "",
          hours: storedStore.hours || null,
          email: storedStore.email || "",
        },
        todayHours: computeTodayHours(storedStore),
      };
    }

    if (!fallbackName) return null;

    return {
      store: {
        id,
        name: fallbackName,
        slug: "",
        address: "",
        phone_number: phone,
      },
      todayHours: "",
    };
  }

  const initialFavoriteStore = getStoredFavoriteStoreSnapshot();
  renderClassesCta(initialFavoriteStore ? initialFavoriteStore.store : null);

  if (!storeRoots.length && classesCtaLinks.length) {
    window.addEventListener("favoriteStoreUpdated", (e) => {
      const store = e.detail && e.detail.store ? e.detail.store : null;
      renderClassesCta(store);
    });
  }

  if (storeRoots.length) {
    storeRoots.forEach((root) => {
      const trigger = root.querySelector("[data-wc-store-trigger]");
      const menu = root.querySelector("[data-wc-store-menu]");
      const label = root.querySelector("[data-wc-store-label]");
      const chev = root.querySelector("[data-wc-store-chev]");
      const findBtn = root.querySelector("[data-wc-find-store]");

      const classesLink = root.querySelector("[data-wc-store-classes]");
      const demosLink = root.querySelector("[data-wc-store-demos]");

      const nameEl = root.querySelector("[data-wc-store-name]");
      const addressEl = root.querySelector("[data-wc-store-address]");
      const phoneEl = root.querySelector("[data-wc-store-phone]");
      const cardEl = root.querySelector("[data-wc-store-card]");

      if (!trigger || !menu || !label || !chev) return;

      let lastStore = null;
      let lastTodayHours = "";

      function openMenu() {
        menu.hidden = false;
        trigger.setAttribute("aria-expanded", "true");
      }

      function closeMenu() {
        menu.hidden = true;
        trigger.setAttribute("aria-expanded", "false");
      }

      function setPillLabel(text, strongText) {
        label.textContent = "";
        label.appendChild(document.createTextNode(text));

        if (strongText) {
          label.appendChild(document.createTextNode(" "));
          const strong = document.createElement("strong");
          strong.textContent = strongText;
          label.appendChild(strong);
        }
      }

      function renderEmpty() {
        lastStore = null;
        lastTodayHours = "";

        setPillLabel("Find a Store", "");
        chev.hidden = true;
        closeMenu();

        if (nameEl) nameEl.textContent = "Woodcraft";
        if (addressEl) addressEl.textContent = "";
        if (phoneEl) phoneEl.textContent = "";
        if (cardEl) cardEl.setAttribute("href", "/pages/store-locator");

        if (classesLink) classesLink.setAttribute("href", "/pages/classes");
        if (demosLink) demosLink.setAttribute("href", "/pages/demos");
        renderClassesCta(null);
      }

      function renderStore(store, todayHours) {
        if (!store) {
          renderEmpty();
          return;
        }

        lastStore = store;
        lastTodayHours = todayHours || computeTodayHours(store);

        const storeName = getShortStoreName(store.name || "");
        setPillLabel(storeName || "Find a Store", lastTodayHours);
        chev.hidden = false;

        if (nameEl) nameEl.textContent = store.name || storeName;

        if (addressEl) {
          if (store.address) {
            addressEl.innerHTML = store.address;
          } else {
            addressEl.textContent = "";
          }
        }

        const phone =
          localStorage.getItem("storeSelectedPhone") ||
          store.phone_number ||
          "";

        if (phoneEl) phoneEl.textContent = phone;

        const classesHref = getStoreClassesHref(store);
        if (classesLink) classesLink.setAttribute("href", classesHref);
        renderClassesCta(store);

        if (store.slug) {
          if (cardEl) cardEl.setAttribute("href", `/pages/store/${store.slug}`);
          if (demosLink) {
            demosLink.setAttribute("href", `/pages/store-events/${store.slug}`);
          }
        }
      }

      trigger.addEventListener("click", () => {
        requestCloseShopAll();

        if (!hasFavorite()) {
          closeMenu();
          openStoreDrawer();
          return;
        }

        if (menu.hidden) openMenu();
        else closeMenu();
      });

      if (findBtn) {
        findBtn.addEventListener("click", () => {
          closeMenu();
          openStoreDrawer();
        });
      }

      document.addEventListener("click", (e) => {
        if (menu.hidden) return;
        if (!root.contains(e.target)) closeMenu();
      });

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && !menu.hidden) closeMenu();
      });

      menu.addEventListener("click", (e) => {
        const link = e.target.closest("a");
        if (link) closeMenu();
      });

      if (initialFavoriteStore && initialFavoriteStore.store) {
        renderStore(
          initialFavoriteStore.store,
          initialFavoriteStore.todayHours,
        );
      } else {
        renderEmpty();
      }

      window.addEventListener("favoriteStoreUpdated", (e) => {
        const store = e.detail && e.detail.store ? e.detail.store : null;
        const todayHours =
          e.detail && e.detail.todayHours ? e.detail.todayHours : "";
        renderStore(store, todayHours);
      });

      const handleResize = () => {
        if (!lastStore) return;
        renderStore(lastStore, lastTodayHours);
      };

      if (mobileMQ && typeof mobileMQ.addEventListener === "function") {
        mobileMQ.addEventListener("change", handleResize);
      } else {
        window.addEventListener("resize", handleResize, { passive: true });
      }
    });
  }

  /*
    ============================
    Shop All drawer logic
    ============================
  */
  const shopAllRoots = document.querySelectorAll("[data-wc-shop-all]");

  function getHeaderOffset() {
    const stickyHeader = document.querySelector(
      "[data-wc-mobile-sticky-header]:not([hidden]).is-visible",
    );

    if (stickyHeader) {
      const stickyRect = stickyHeader.getBoundingClientRect();
      return Math.max(0, Math.round(stickyRect.bottom));
    }

    const header = document.querySelector(".wc-header");
    if (!header) return 0;

    const rect = header.getBoundingClientRect();
    return Math.max(0, Math.round(rect.bottom));
  }

  function setHeaderOffsetVars() {
    const offset = getHeaderOffset();
    document.documentElement.style.setProperty(
      "--wc-header-bottom-offset",
      `${offset}px`,
    );
    document.documentElement.style.setProperty(
      "--wc-header-bottom-offset-mobile",
      `${offset}px`,
    );
  }

  if (shopAllRoots.length) {
    setHeaderOffsetVars();
    window.addEventListener("resize", setHeaderOffsetVars, { passive: true });
    window.addEventListener("load", setHeaderOffsetVars);

    shopAllRoots.forEach((shopAllRoot) => {
      const panel = shopAllRoot.querySelector("[data-wc-shop-all-panel]");
      if (!panel || !panel.id) return;

      const header = document.querySelector(".wc-header");
      const searchArea = header ? header.querySelector(".wc-search") : null;
      const actionsArea = header ? header.querySelector(".wc-actions") : null;
      const topArea = header ? header.querySelector(".wc-header__top") : null;
      const bottomArea = header
        ? header.querySelector(".wc-header__bottom")
        : null;

      const triggers = document.querySelectorAll(
        `[data-wc-shop-all-trigger][aria-controls="${panel.id}"]`,
      );

      const closeButtons = shopAllRoot.querySelectorAll(
        "[data-wc-shop-all-close]",
      );
      const backButton = panel.querySelector("[data-wc-shop-all-back]");
      const titleEl = panel.querySelector("[data-wc-shop-all-title]");
      const dividerEl = panel.querySelector("[data-wc-shop-all-divider]");
      const viewsEl = panel.querySelector("[data-wc-shop-all-views]");
      const rootView = panel.querySelector('[data-wc-shop-all-view="root"]');

      if (!triggers.length || !titleEl || !viewsEl || !rootView) return;

      let lastFocusedElement = null;
      let navStack = [];
      let cleanupTimer = null;
      let isTransitioning = false;
      const rootTitle = titleEl.textContent.trim();
      const viewTransitionMs = 260;
      const focusableSelector = [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ].join(",");

      function isOpen() {
        return !shopAllRoot.hidden;
      }

      function lockBodyScroll() {
        document.documentElement.classList.add("wc-shop-all-open");
        document.body.classList.add("wc-shop-all-open");
      }

      function unlockBodyScroll() {
        document.documentElement.classList.remove("wc-shop-all-open");
        document.body.classList.remove("wc-shop-all-open");
      }

      function setTriggersExpanded(isExpanded) {
        triggers.forEach((trigger) => {
          trigger.setAttribute("aria-expanded", isExpanded ? "true" : "false");
        });
      }

      function getActiveView() {
        const activeView = viewsEl.querySelector(
          ".wc-shop-all__view.is-active",
        );
        return activeView || rootView;
      }

      function getFocusablePanelElements() {
        return Array.from(panel.querySelectorAll(focusableSelector)).filter(
          (element) => {
            if (element.closest("[hidden]")) return false;
            if (element.getAttribute("aria-hidden") === "true") return false;

            return Boolean(
              element.offsetWidth ||
                element.offsetHeight ||
                element.getClientRects().length,
            );
          },
        );
      }

      function trapPanelFocus(event) {
        if (event.key !== "Tab") return;

        const focusableElements = getFocusablePanelElements();

        if (!focusableElements.length) {
          event.preventDefault();
          panel.focus();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;
        const focusIsInsidePanel = panel.contains(activeElement);

        if (event.shiftKey) {
          if (!focusIsInsidePanel || activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }

          return;
        }

        if (!focusIsInsidePanel || activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }

      function updateToolbar() {
        const atRoot = navStack.length === 0;
        const currentTitle = atRoot
          ? rootTitle
          : navStack[navStack.length - 1].title;

        titleEl.textContent = currentTitle;

        if (backButton) {
          backButton.hidden = atRoot;
        }

        if (dividerEl) {
          dividerEl.classList.toggle("is-hidden", atRoot);
        }
      }

      function clearTransientViews() {
        if (cleanupTimer) {
          window.clearTimeout(cleanupTimer);
          cleanupTimer = null;
        }
        isTransitioning = false;

        const views = viewsEl.querySelectorAll("[data-wc-shop-all-view]");
        views.forEach((view) => {
          if (view !== rootView) {
            view.remove();
          }
        });

        rootView.hidden = false;
        rootView.className = "wc-shop-all__view is-active";
      }

      function resetDrawer() {
        navStack = [];
        clearTransientViews();
        updateToolbar();
      }

      function focusFirstViewControl(view) {
        const firstFocusable = view.querySelector("a, button");
        if (!firstFocusable) return;

        try {
          firstFocusable.focus({ preventScroll: true });
        } catch (_err) {
          firstFocusable.focus();
        }
      }

      function animateToView(currentView, nextView, direction, onComplete) {
        if (!currentView || !nextView || currentView === nextView) return;
        isTransitioning = true;

        if (cleanupTimer) {
          window.clearTimeout(cleanupTimer);
          cleanupTimer = null;
        }

        nextView.hidden = false;

        currentView.classList.remove(
          "is-entering-forward",
          "is-entering-back",
          "is-leaving-forward",
          "is-leaving-back",
        );
        nextView.classList.remove(
          "is-entering-forward",
          "is-entering-back",
          "is-leaving-forward",
          "is-leaving-back",
        );

        if (direction === "forward") {
          nextView.classList.add("is-entering-forward");
          currentView.classList.add("is-leaving-forward");
        } else {
          nextView.classList.add("is-entering-back");
          currentView.classList.add("is-leaving-back");
        }

        nextView.getBoundingClientRect();

        window.requestAnimationFrame(() => {
          currentView.classList.remove("is-active");
          nextView.classList.add("is-active");
        });

        cleanupTimer = window.setTimeout(() => {
          currentView.hidden = true;
          currentView.classList.remove("is-leaving-forward", "is-leaving-back");
          nextView.classList.remove("is-entering-forward", "is-entering-back");
          isTransitioning = false;
          cleanupTimer = null;

          if (typeof onComplete === "function") {
            onComplete();
          }
        }, viewTransitionMs);
      }

      function buildViewFromSubmenu(submenu) {
        const title =
          submenu.getAttribute("data-wc-shop-all-title") || rootTitle;

        const view = document.createElement("div");
        view.className = "wc-shop-all__view";
        view.hidden = true;
        view.setAttribute("data-wc-shop-all-view", title);

        const inner = document.createElement("div");
        inner.className = "wc-shop-all__view-inner";
        inner.innerHTML = submenu.innerHTML;

        inner
          .querySelectorAll("[data-wc-shop-all-subtoggle]")
          .forEach((toggle) => {
            delete toggle.dataset.wcShopAllBound;
            toggle.removeAttribute("data-wc-shop-all-bound");
          });

        view.appendChild(inner);
        viewsEl.appendChild(view);

        wireToggleHandlers(view);

        return {
          view,
          title,
        };
      }

      function openLevel(submenu) {
        if (!submenu || isTransitioning) return;

        const currentView = getActiveView();
        const built = buildViewFromSubmenu(submenu);

        navStack.push({
          title: built.title,
          view: built.view,
        });

        updateToolbar();
        animateToView(currentView, built.view, "forward", () => {
          focusFirstViewControl(built.view);
        });
      }

      function goBack() {
        if (!navStack.length || isTransitioning) return;

        const leavingEntry = navStack.pop();
        const currentView = leavingEntry.view;
        const previousEntry = navStack[navStack.length - 1];
        const nextView = previousEntry ? previousEntry.view : rootView;

        updateToolbar();

        animateToView(currentView, nextView, "back", () => {
          if (leavingEntry.view && leavingEntry.view.parentNode) {
            leavingEntry.view.remove();
          }
          focusFirstViewControl(nextView);
        });
      }

      function getControlledSubmenu(toggle, scope) {
        const controlsId = toggle.getAttribute("aria-controls");
        if (!controlsId) return null;

        const escapedId =
          typeof CSS !== "undefined" && CSS.escape
            ? CSS.escape(controlsId)
            : controlsId.replace(/([ #;?%&,.+*~':"!^$[\]()=>|/@])/g, "\\$1");

        return (
          scope.querySelector(`#${escapedId}`) ||
          panel.querySelector(`#${escapedId}`)
        );
      }

      function wireToggleHandlers(scope) {
        const toggles = scope.querySelectorAll("[data-wc-shop-all-subtoggle]");

        toggles.forEach((toggle) => {
          if (toggle.dataset.wcShopAllBound === "true") return;
          toggle.dataset.wcShopAllBound = "true";

          toggle.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            const submenu = getControlledSubmenu(toggle, scope);
            if (!submenu) return;

            openLevel(submenu);
          });
        });
      }

      function openDrawer() {
        lastFocusedElement = document.activeElement;
        setHeaderOffsetVars();
        resetDrawer();

        shopAllRoot.hidden = false;
        setTriggersExpanded(true);
        lockBodyScroll();

        window.requestAnimationFrame(() => {
          shopAllRoot.classList.add("is-open");
          panel.focus();
        });
      }

      function closeDrawer() {
        if (!isOpen()) return;

        shopAllRoot.classList.remove("is-open");
        setTriggersExpanded(false);
        unlockBodyScroll();

        window.setTimeout(() => {
          shopAllRoot.hidden = true;
          resetDrawer();

          if (
            lastFocusedElement &&
            typeof lastFocusedElement.focus === "function"
          ) {
            lastFocusedElement.focus();
          }
        }, 220);
      }

      function toggleDrawer() {
        if (shopAllRoot.hidden) openDrawer();
        else closeDrawer();
      }

      function bindCloseOnInteraction(container, selector) {
        if (!container) return;

        container.addEventListener("pointerdown", (e) => {
          if (!isOpen()) return;
          if (panel.contains(e.target)) return;

          const target = e.target.closest(selector);
          if (!target) return;

          if (target.closest("[data-wc-shop-all-trigger]")) return;

          requestCloseShopAll();
        });
      }

      wireToggleHandlers(panel);

      triggers.forEach((trigger) => {
        trigger.addEventListener("click", (e) => {
          e.stopPropagation();
          toggleDrawer();
        });
      });

      closeButtons.forEach((button) => {
        button.addEventListener("click", () => {
          closeDrawer();
        });
      });

      if (backButton) {
        backButton.addEventListener("click", () => {
          goBack();
        });
      }

      panel.addEventListener("click", (e) => {
        const link = e.target.closest("a");
        if (!link) return;
        closeDrawer();
      });

      window.addEventListener("wc:close-shop-all", () => {
        closeDrawer();
      });

      document.addEventListener("keydown", (e) => {
        if (!isOpen()) return;

        if (e.key === "Escape") {
          e.preventDefault();
          closeDrawer();
          return;
        }

        trapPanelFocus(e);

        if (e.key === "Backspace") {
          const activeEl = document.activeElement;
          const tagName =
            activeEl && activeEl.tagName ? activeEl.tagName.toLowerCase() : "";
          const isTyping =
            tagName === "input" ||
            tagName === "textarea" ||
            (activeEl && activeEl.isContentEditable);

          if (!isTyping && navStack.length) {
            e.preventDefault();
            goBack();
          }
        }
      });

      if (searchArea) {
        searchArea.addEventListener("pointerdown", (e) => {
          if (!isOpen()) return;
          if (panel.contains(e.target)) return;
          requestCloseShopAll();
        });

        const searchInput = searchArea.querySelector(
          'input[type="search"], input[name="q"]',
        );
        if (searchInput) {
          searchInput.addEventListener("focus", () => {
            if (!isOpen()) return;
            requestCloseShopAll();
          });
        }
      }

      bindCloseOnInteraction(topArea, "a, button");
      bindCloseOnInteraction(actionsArea, "a, button");
      bindCloseOnInteraction(bottomArea, "a, button");

      document.addEventListener("pointerdown", (e) => {
        if (!isOpen()) return;
        if (panel.contains(e.target)) return;

        const shopAllTrigger = e.target.closest("[data-wc-shop-all-trigger]");
        if (shopAllTrigger) return;

        const storeTrigger = e.target.closest("[data-wc-store-trigger]");
        const isHeaderLinkOrButton = e.target.closest(
          ".wc-header__top a, .wc-header__top button, .wc-header__bottom a, .wc-header__bottom button",
        );

        if (storeTrigger || isHeaderLinkOrButton) {
          requestCloseShopAll();
        }
      });
    });
  }
})();
